/** Deterministic background/text color for a tag category, keyed by `code`
 *  (not id) — the same category code always gets the same color, including
 *  across different tenants that happen to share a code (e.g. "departure").
 *
 * Why hash-of-code into a fixed palette, not a DB field: a DB field would
 * need per-tenant configuration (or a global table someone has to maintain
 * and keep in sync as new codes appear), and would still need a fallback for
 * codes nobody's configured yet. A pure function of the code string needs no
 * storage, no migration, and is consistent for every viewer everywhere by
 * construction — "consistent for different people in different
 * circumstances" falls out for free. `overrides` is the seam for later
 * making this configurable (e.g. from a future per-tenant color map) without
 * changing the function's contract or any caller.
 */

export interface CategoryColor {
  bg: string;
  fg: string;
}

// Curated, mutually-distinguishable pastel/text pairs — deliberately more
// numerous than MUI's ~7 named colors (tenants accumulate far more than 7
// category codes between them, so a small palette would collide constantly).
const PALETTE: CategoryColor[] = [
  { bg: '#E3F2FD', fg: '#0D47A1' }, // blue
  { bg: '#E8F5E9', fg: '#1B5E20' }, // green
  { bg: '#FFF3E0', fg: '#E65100' }, // orange
  { bg: '#FCE4EC', fg: '#880E4F' }, // pink
  { bg: '#F3E5F5', fg: '#4A148C' }, // purple
  { bg: '#E0F7FA', fg: '#006064' }, // cyan
  { bg: '#FFFDE7', fg: '#827717' }, // yellow
  { bg: '#EFEBE9', fg: '#3E2723' }, // brown
  { bg: '#E8EAF6', fg: '#1A237E' }, // indigo
  { bg: '#FBE9E7', fg: '#BF360C' }, // deep orange
  { bg: '#E0F2F1', fg: '#004D40' }, // teal
  { bg: '#FFEBEE', fg: '#B71C1C' }, // red
  { bg: '#F1F8E9', fg: '#33691E' }, // light green
  { bg: '#EDE7F6', fg: '#311B92' }, // deep purple
  { bg: '#FFF8E1', fg: '#FF6F00' }, // amber
  { bg: '#ECEFF1', fg: '#263238' }, // blue grey
];

/** djb2 string hash — simple, fast, and (unlike Math.random or Date-seeded
 *  hashes) gives the exact same result for the exact same string on every
 *  run, every machine, forever. */
function hashCode(s: string): number {
  let hash = 5381;
  for (let i = 0; i < s.length; i++) {
    hash = (hash * 33) ^ s.charCodeAt(i);
  }
  return hash >>> 0; // unsigned, for a well-defined modulo below
}

/** Resolve a category code to its color. Case-insensitive (codes are
 *  lowercase by convention, but this is defensive). Falls back to a stable
 *  slot for an empty/missing code so "uncategorized" tags still render
 *  consistently rather than crashing on `''`. */
export function categoryColor(
  code: string | null | undefined,
  overrides?: Record<string, CategoryColor>,
): CategoryColor {
  const key = (code ?? '').trim().toLowerCase();
  if (overrides && key in overrides) return overrides[key]!;
  return PALETTE[hashCode(key) % PALETTE.length]!;
}
